import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import addDays from 'date-fns/addDays'

import { getStripePrice } from '~/subscription/apis/subscription.api'
import { IUser, MobileAppSubscriptionStatus } from '~/types/user-info-types'
import { RootState } from '~/redux/store'
import { getMe } from '~/redux/apis/user-api'
import { userHasSubscription } from '~/redux/utils/subscription'
import { getPromotion, subscriptionApplicability } from '~/redux/apis/payment-api'
import appConfig from '~/config/app'
import { logout } from '~/auth/slices/auth.slice'

interface SubscriptionState {
  subscriptionSuccessRedirectUrl: string
  subscriptionCoupon?: string
  subscriptionPromoCode?: string
  subscriptionCampaignPrice?: string
  subscriptionProduct: any
  currentSubscriptionPriceId: string
  currentSubscriptionId: string
  subscriptionCouponExpires?: string
  subscriptionCampaignPriceExpires?: string
  allowTrial: boolean
  isGa4Logged: boolean
  isSubscriptionApplicable: boolean
  subscriptionPromotionId?: string
  showSubscriptionPackages?: boolean
  subscriptionPromoCouponId?: string
  subscriptionPackageType?: 'individual' | 'family'
  subscriptionIntervalType?: 'month' | 'year'
}

const initialState: SubscriptionState = {
  subscriptionSuccessRedirectUrl: '',
  subscriptionCoupon: undefined,
  subscriptionPromoCode: undefined,
  subscriptionCampaignPrice: undefined,
  subscriptionProduct: {},
  currentSubscriptionPriceId: '',
  currentSubscriptionId: '',
  subscriptionCouponExpires: undefined,
  subscriptionCampaignPriceExpires: undefined,
  allowTrial: false,
  isGa4Logged: false,
  isSubscriptionApplicable: false,
  subscriptionPromotionId: undefined,
  showSubscriptionPackages: false,
  subscriptionPromoCouponId: undefined,
  subscriptionPackageType: 'family', // Default value, can be changed later
  subscriptionIntervalType: 'year', // Default value, can be changed later
}

const slice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    setSubscriptionSuccessRedirectUrl: (state, action: PayloadAction<string>) => ({
      ...state,
      subscriptionSuccessRedirectUrl: action.payload,
    }),
    setSubscriptionCoupon: (state, action: PayloadAction<string | undefined>) => ({
      ...state,
      subscriptionCoupon: action.payload,
      subscriptionCouponExpires: undefined,
    }),
    // Clears every field derived from a previously-applied coupon/promotion so a fresh checkout visit
    // starts clean (no stale discount, no flash of the "not applicable" error). The next coupon is
    // re-applied from the checkout URL. Mirrors the manual "clear coupon" action's state resets.
    resetAppliedCoupon: (state) => ({
      ...state,
      subscriptionCoupon: undefined,
      subscriptionCouponExpires: undefined,
      subscriptionPromoCode: undefined,
      subscriptionPromotionId: undefined,
      subscriptionPromoCouponId: undefined,
    }),
    setSubscriptionCampaignPrice: (state, action: PayloadAction<string | undefined>) => ({
      ...state,
      subscriptionCampaignPrice: action.payload,
      subscriptionCampaignPriceExpires: addDays(new Date(), 30).toISOString(),
    }),
    setAllowTrial: (state, action: PayloadAction<boolean>) => ({
      ...state,
      allowTrial: action.payload,
    }),
    setSubscriptionProduct: (state, action: PayloadAction<any>) => ({
      ...state,
      subscriptionProduct: action.payload,
    }),
    setCurrentSubscriptionPriceId: (state, action: PayloadAction<any>) => ({
      ...state,
      currentSubscriptionPriceId: action.payload,
    }),
    setSubscriptionPromotionId: (state, action: PayloadAction<SubscriptionState['subscriptionPromotionId']>) => ({
      ...state,
      subscriptionPromotionId: action.payload,
    }),
    setCurrentSubscriptionId: (state, action: PayloadAction<any>) => ({
      ...state,
      currentSubscriptionId: action.payload,
    }),
    setIsGa4Logged: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isGa4Logged: action.payload,
    }),
    setShowSubscriptionPackages: (state, action: PayloadAction<boolean>) => ({
      ...state,
      showSubscriptionPackages: action.payload,
    }),

    setSubscriptionPromoCode: (state, action: PayloadAction<string | undefined>) => ({
      ...state,
      subscriptionPromoCode: action.payload,
    }),
    setSubscriptionPromoCouponId: (state, action: PayloadAction<string | undefined>) => ({
      ...state,
      subscriptionPromoCouponId: action.payload,
    }),
    setSubscriptionPackageType: (state, action: PayloadAction<SubscriptionState['subscriptionPackageType']>) => ({
      ...state,
      subscriptionPackageType: action.payload,
    }),
    setSubscriptionIntervalType: (state, action: PayloadAction<SubscriptionState['subscriptionIntervalType']>) => ({
      ...state,
      subscriptionIntervalType: action.payload,
    }),
    setActiveIndex: (state, action: PayloadAction<number | undefined>) => ({
      ...state,
      activeIndex: action.payload,
    }),
  },
  extraReducers: (builder) => {
    return builder
      .addCase(logout, () => initialState)
      .addMatcher(getStripePrice.matchFulfilled, (state, action: PayloadAction<any>) => {
        state.subscriptionProduct = action.payload
        state.isGa4Logged = false
        if (state.subscriptionCoupon) {
          state.subscriptionCouponExpires = addDays(new Date(), 1).toISOString()
        } else {
          state.subscriptionCouponExpires = undefined
        }
      })
      .addMatcher(getMe.matchFulfilled, (state, action: PayloadAction<IUser>) => {
        if (userHasSubscription(action.payload)) {
          state.currentSubscriptionPriceId = action.payload.stripeSubscription[0]?.stripePriceId || ''
          state.currentSubscriptionId = action.payload.stripeSubscription[0]?.subscriptionId || ''
        } else if (appConfig.isMobileApp) {
          state.isSubscriptionApplicable =
            action.payload.validMobileAppSubscription?.subscriptionStatus === MobileAppSubscriptionStatus.ACTIVE ||
            action.payload.validMobileAppSubscription?.subscriptionStatus === MobileAppSubscriptionStatus.TRIALING
        } else {
          state.currentSubscriptionPriceId = ''
          state.currentSubscriptionId = ''
        }
      })
      .addMatcher(subscriptionApplicability.matchFulfilled, (state, action: PayloadAction<boolean>) => {
        state.isSubscriptionApplicable = action.payload
      })
      .addMatcher(getPromotion.matchFulfilled, (state, action: PayloadAction<{ promotionId: string; coupon: any }>) => {
        state.subscriptionPromotionId = action.payload.promotionId
        state.subscriptionPromoCouponId = action.payload.coupon.id
      })
  },
})

export const selectSubscriptionSuccessRedirectUrl = (state: RootState): string => {
  return state.subscription.subscriptionSuccessRedirectUrl
}

export const selectSubscriptionProduct = (state: RootState): any => {
  return state.subscription.subscriptionProduct
}

export const selectCurrentSubscriptionPriceId = (state: RootState): string => {
  return state.subscription.currentSubscriptionPriceId
}
export const selectCurrentSubscriptionId = (state: RootState): string => {
  return state.subscription.currentSubscriptionId
}

export const selectSubscriptionCoupon = (state: RootState): string | undefined => {
  return state.subscription.subscriptionCoupon
}

export const selectSubscriptionPromotion = (state: RootState): string | undefined => {
  return state.subscription.subscriptionPromotionId
}

export const selectSubscriptionCampaignPrice = (state: RootState): string | undefined => {
  return state.subscription.subscriptionCampaignPrice
}

export const selectIsLogged = (state: RootState): boolean => {
  return state.subscription.isGa4Logged
}

export const subscriptionCouponExpires = (state: RootState): string | undefined => {
  return state.subscription.subscriptionCouponExpires
}

export const subscriptionCampaignPriceExpires = (state: RootState): string | undefined => {
  return state.subscription.subscriptionCampaignPriceExpires
}

export const selectIsSubscriptionApplicable = (state: RootState): boolean => {
  // The school platform sells nothing, so no pricing or upgrade surface applies. This one selector has
  // 10 consumers — it is what switches off the Footer and landing-menu pricing links, the
  // stats-to-pricing redirect in MenuHorizontal and the Subscription button in the menu.
  if (appConfig.isSchoolPlatform) return false

  return state.subscription.isSubscriptionApplicable
}

export const selectShowSubscriptionPackages = (state: RootState): boolean => {
  return Boolean(state.subscription.showSubscriptionPackages)
}

export const selectSubscriptionPromoCode = (state: RootState): string | undefined => {
  return state.subscription.subscriptionPromoCode
}

export const selectSubscriptionPackageType = (state: RootState): SubscriptionState['subscriptionPackageType'] => {
  return state.subscription.subscriptionPackageType
}

export const selectSubscriptionIntervalType = (state: RootState): SubscriptionState['subscriptionIntervalType'] => {
  return state.subscription.subscriptionIntervalType
}

export const selectSubscriptionPromoCouponId = (state: RootState): string | undefined => {
  return state.subscription.subscriptionPromoCouponId
}

export const {
  setSubscriptionSuccessRedirectUrl,
  setSubscriptionCoupon,
  resetAppliedCoupon,
  setSubscriptionCampaignPrice,
  setSubscriptionProduct,
  setCurrentSubscriptionId,
  setCurrentSubscriptionPriceId,
  setIsGa4Logged,
  setSubscriptionPromotionId,
  setShowSubscriptionPackages,
  setSubscriptionPromoCode,
  setSubscriptionPromoCouponId,
  setSubscriptionPackageType,
  setSubscriptionIntervalType,
} = slice.actions

export default slice.reducer
